import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Send, CheckCheck, Check, CalendarPlus, AlertTriangle } from 'lucide-react';
import { trainerChatAPI } from '@/lib/api';

/* Trainer-Chat (29.10.2026, backend/trainer_chat.py). Die Leitung schreibt im CRM (z. B. montags die Woche:
   „Mo 10 Max Muster“ je Zeile), daraus werden PT-Termine im Kalender des Trainers. Der Trainer liest im Link,
   „gelesen“ steht danach unter jeder Nachricht. */

const STATUS = { angefragt: 'wartet auf Zusage', geplant: 'steht fest', erledigt: 'erledigt', abgesagt: 'abgesagt', abgelehnt: 'abgelehnt' };
const uhr = (t) => t ? new Date(t).toLocaleString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
const tagDE = (d) => new Date(d + 'T12:00').toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });

export function ChatVerlauf({ nachrichten, eigeneSeite }) {
  const ende = useRef(null);
  useEffect(() => { ende.current?.scrollIntoView({ block: 'end' }); }, [nachrichten.length]);
  if (!nachrichten.length) return <p className="text-xs text-muted-foreground py-6 text-center">Noch keine Nachrichten.</p>;
  return (
    <div className="space-y-2" data-testid="chat-verlauf">
      {nachrichten.map(n => {
        const eigen = n.von === eigeneSeite;
        const gelesen = n.von === 'leitung' ? n.gelesen_am : n.gelesen_leitung_am;
        return (
          <div key={n.id} className={`flex ${eigen ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${eigen ? 'bg-marke text-white rounded-br-sm' : 'bg-muted rounded-bl-sm'}`}>
              <p className="whitespace-pre-wrap break-words">{n.text}</p>
              {n.termine?.length > 0 && (
                <div className={`mt-2 space-y-1 rounded-lg p-2 text-[11px] ${eigen ? 'bg-white/15' : 'bg-background/70'}`}>
                  {n.termine.map(t => <p key={t.id} className="flex gap-2"><CalendarPlus className="w-3 h-3 mt-0.5 shrink-0" /><span>{tagDE(t.datum)} {t.start}–{t.ende} · {t.kunde || 'PT'} · <b>{STATUS[t.status] || t.status}</b></span></p>)}
                </div>
              )}
              {n.nicht_eingetragen?.length > 0 && (
                <div className="mt-1 text-[11px] space-y-0.5">{n.nicht_eingetragen.map((x, i) => <p key={i} className="flex gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />nicht eingetragen: {x.zeile} ({x.grund})</p>)}</div>
              )}
              <p className={`text-[10px] mt-1 flex items-center gap-1 ${eigen ? 'text-white/80 justify-end' : 'text-muted-foreground'}`}>
                {n.von === 'leitung' && n.erstellt_von && <span>{String(n.erstellt_von).split(' ')[0]} ·</span>}
                {uhr(n.erstellt_am)}
                {eigen && (gelesen ? <span className="flex items-center gap-0.5" data-testid="chat-gelesen"><CheckCheck className="w-3 h-3" /> gelesen {new Date(gelesen).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span> : <span className="flex items-center gap-0.5"><Check className="w-3 h-3" /> zugestellt</span>)}
              </p>
            </div>
          </div>
        );
      })}
      <div ref={ende} />
    </div>
  );
}

/* Im Trainer-Link (ohne Login): Öffnen markiert die Nachrichten der Leitung als gelesen */
export function TrainerChatLink({ token, onGelesen }) {
  const [nachrichten, setNachrichten] = useState(null);
  const [text, setText] = useState('');
  const [arbeitet, setArbeitet] = useState(false);
  const lade = useCallback(async () => { try { setNachrichten((await trainerChatAPI.linkLesen(token)).data.nachrichten || []); onGelesen?.(); } catch { setNachrichten([]); } }, [token, onGelesen]);
  useEffect(() => { lade(); const t = setInterval(lade, 30000); return () => clearInterval(t); }, [lade]);
  const senden = async () => {
    if (!text.trim()) return;
    setArbeitet(true);
    try { await trainerChatAPI.linkAntworten(token, text.trim()); setText(''); lade(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Senden fehlgeschlagen'); }
    finally { setArbeitet(false); }
  };
  return (
    <div className="max-w-2xl mx-auto px-3 py-3 space-y-3" data-testid="link-chat">
      {nachrichten === null ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div> : <ChatVerlauf nachrichten={nachrichten} eigeneSeite="trainer" />}
      <p className="text-[11px] text-muted-foreground">Termine aus dem Chat stehen fest in deinem Kalender.</p>
      <div className="flex gap-2 sticky bottom-2">
        <textarea value={text} onChange={e => setText(e.target.value)} rows={2} placeholder="Antwort ans Studio …" className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm" />
        <button type="button" onClick={senden} disabled={arbeitet || !text.trim()} className="h-11 w-11 self-end rounded-xl bg-[#E60000] text-white flex items-center justify-center disabled:opacity-50" aria-label="Senden">{arbeitet ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
      </div>
    </div>
  );
}
